import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, Wrench, MapPin, Package, Search } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { useToast } from '@/hooks/use-toast';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { Equipment, Location } from '@/types/settings';

export default function EquipmentPage(): React.ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        const [equipmentData, locationsData] = await Promise.all([
          dataService.getEquipment(),
          dataService.getLocations(),
        ]);
        setEquipment(equipmentData);
        setLocations(locationsData);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load equipment. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this equipment?')) {
      try {
        if (isDemoMode()) {
          setEquipment(equipment.filter((eq) => eq.id !== id));
        } else {
          await dataService.deleteEquipment(id);
          setEquipment(equipment.filter((eq) => eq.id !== id));
        }
        toast({
          title: 'Success',
          description: 'Equipment deleted successfully',
        });
      } catch (err) {
        console.error('Failed to delete equipment:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete equipment. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const filteredEquipment = equipment.filter((eq) =>
    eq.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    eq.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getLocationName = (locationId?: string) => {
    const location = locations.find((loc) => loc.id === locationId);
    return location?.name ?? '-';
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} className="text-center text-muted-foreground h-32">
            Loading equipment...
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={7} className="text-center text-destructive h-32">
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (filteredEquipment.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} className="text-center text-muted-foreground h-32">
            No equipment found
          </TableCell>
        </TableRow>
      );
    }

    return filteredEquipment.map((eq, index) => (
      <TableRow 
        key={eq.id}
        className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
          index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'
        }`}
      >
        <TableCell className="font-medium">
          <button
            onClick={() => navigate(`/administration/equipment/${eq.id}`)}
            className="text-primary hover:underline cursor-pointer font-medium"
          >
            {eq.code}
          </button>
        </TableCell>
        <TableCell>{eq.description ?? '-'}</TableCell>
        <TableCell className="text-right">
          {formatCurrency(eq.acquisitionValue)}
        </TableCell>
        <TableCell>
          {eq.acquiredDate
            ? new Date(eq.acquiredDate).toLocaleDateString()
            : '-'}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 text-muted-foreground" />
            {getLocationName(eq.locationId)}
          </div>
        </TableCell>
        <TableCell>
          {eq.linkedAssetId ? (
            <div className="flex items-center gap-1">
              <Package className="h-3 w-3 text-muted-foreground" />
              {eq.linkedAssetId}
            </div>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/administration/equipment/${eq.id}`)}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(eq.id)}
              className="text-destructive hover:text-destructive"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Equipment</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage equipment records and supplier information
              </p>
            </div>
          </div>
                    <div className="flex gap-2 items-center">
                      <EvoFeatureFeedbackCollector feature="Equipment" />
                      <div className="relative w-80">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Search equipment..."
                          value={searchQuery}
                          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <Button onClick={() => navigate('/administration/equipment/new')}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Equipment
                      </Button>
                    </div>
                  </div>
                </div>

            {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
              <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                <TableHead className="h-12 font-semibold">Code</TableHead>
                <TableHead className="h-12 font-semibold">Description</TableHead>
                <TableHead className="text-right h-12 font-semibold">Acquisition Value</TableHead>
                <TableHead className="h-12 font-semibold">Acquired Date</TableHead>
                <TableHead className="h-12 font-semibold">Location</TableHead>
                <TableHead className="h-12 font-semibold">Linked Asset</TableHead>
                <TableHead className="text-right h-12 font-semibold"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
